import React from 'react';
import { Check, Info, AlertCircle } from 'lucide-react';
import { resolveLogoUrl } from '../../../lib/resolveLogoUrl';

/**
 * Le patron « page publique » (maquette 22a, Pages Publiques) — une forme pour les onze pages à jeton :
 *   1. en-tête aux couleurs de l'établissement (logo, nom, dossier) — jamais de logo L'IAmani ici ;
 *   2. ce qu'on demande au visiteur, à la deuxième personne, avec la date limite en clair ;
 *   3. le contenu à lire ou remplir (cibles de 44 px, texte à 12,5 px minimum : ça se remplit au téléphone) ;
 *   4. une seule action pleine, au plus un bouton secondaire bordé ;
 *   5. un état final explicite — envoyé, signé, clos, expiré (22e).
 * Première application : le Portail Organisateur (lot 10). Les zones 2 à 4 sont le contenu de la page ;
 * ce fichier fournit la coquille (1), l'état final (5) et les petites pièces communes.
 */

export interface Etablissement { name: string; logo_url?: string | null; theme_color?: string | null }

/** Initiales de repli quand l'établissement n'a pas de logo : les deux premiers mots pleins — « BV » pour Brasserie du Vieux Port. */
export const initiales = (nom: string) => {
    const mots = String(nom || '').split(/\s+/).filter((m) => m.length > 2);
    return (mots.length >= 2 ? mots[0][0] + mots[1][0] : String(nom || '?').slice(0, 2)).toUpperCase();
};

export function LogoEtablissement({ etablissement, taille = 44 }: { etablissement: Etablissement; taille?: number }) {
    const logo = etablissement.logo_url ? resolveLogoUrl(etablissement.logo_url) : '';
    return (
        <span className="inline-flex items-center justify-center shrink-0 rounded-xl bg-surface border border-border text-[15px] font-bold tracking-[-0.01em] text-content overflow-hidden"
            style={{ width: taille, height: taille, borderRadius: Math.round(taille * 0.27) }}>
            {logo ? <img src={logo} alt={etablissement.name} className="w-full h-full object-contain p-0.5" /> : initiales(etablissement.name)}
        </span>
    );
}

/**
 * La coquille : fond de l'app, colonne centrée à la largeur demandée (440 à 620 selon la page, 880 pour
 * le portail), couleur de l'établissement posée en `--acc` pour toute la page.
 */
export function PagePublique({ etablissement, largeur = 440, children }: { etablissement?: Etablissement | null; largeur?: number; children: React.ReactNode }) {
    const style = { maxWidth: largeur, ...(etablissement?.theme_color ? { ['--acc' as any]: etablissement.theme_color } : {}) } as React.CSSProperties;
    return (
        <div className="min-h-screen bg-surface-app px-4 py-6 sm:py-10">
            <div className="mx-auto w-full" style={style}>{children}</div>
        </div>
    );
}

/** Zone 1, forme centrée (19a, 22b) : logo, nom, une ligne qui dit de quoi il s'agit. */
export function EnTeteEtablissement({ etablissement, sousTitre }: { etablissement: Etablissement; sousTitre: React.ReactNode }) {
    return (
        <div className="px-5 py-5 border-b border-border-soft bg-surface-soft text-center">
            <LogoEtablissement etablissement={etablissement} />
            <div className="mt-2.5 text-[13px] font-bold text-content">{etablissement.name}</div>
            <div className="mt-px text-[11.5px] text-content-muted">{sousTitre}</div>
        </div>
    );
}

/** La carte blanche des pages à une colonne (440 px) : en-tête + corps. */
export function CartePublique({ children }: { children: React.ReactNode }) {
    return <div className="rounded-[14px] bg-surface border border-border overflow-hidden">{children}</div>;
}

export type TonEtat = 'succes' | 'info' | 'attention' | 'danger' | 'neutre';

export const TON_ETAT: Record<TonEtat, { bord: string; fond: string; texte: string }> = {
    succes: { bord: 'border-state-ok/30', fond: 'bg-state-ok-soft', texte: 'text-state-ok' },
    info: { bord: 'border-state-info/30', fond: 'bg-state-info-soft', texte: 'text-state-info' },
    attention: { bord: 'border-state-warn/30', fond: 'bg-state-warn-soft', texte: 'text-state-warn' },
    danger: { bord: 'border-state-danger/30', fond: 'bg-state-danger-soft', texte: 'text-state-danger' },
    neutre: { bord: 'border-border', fond: 'bg-surface-soft', texte: 'text-content-muted' },
};

/**
 * Zone 5 — l'état final (22e / 19e) : un bandeau teinté avec son icône, le titre, une phrase qui dit
 * ce qui s'est passé, et ce que le visiteur peut faire. Valable pour les onze pages.
 */
export function EtatFinal({ ton, icone = 'alerte', titre, texte, action }: {
    ton: TonEtat;
    icone?: 'coche' | 'info' | 'alerte';
    titre: React.ReactNode;
    texte: React.ReactNode;
    /** Le geste possible : un texte simple, ou un bouton / lien déjà rendu. */
    action?: React.ReactNode;
}) {
    const t = TON_ETAT[ton];
    const Icone = icone === 'coche' ? Check : icone === 'info' ? Info : AlertCircle;
    return (
        <div className={`rounded-xl bg-surface border ${t.bord} overflow-hidden`}>
            <div className={`px-4 py-3.5 ${t.fond} border-b ${t.bord}`}>
                <div className="flex items-center gap-[9px]">
                    <span className={`w-6 h-6 shrink-0 rounded-[7px] bg-surface border ${t.bord} ${t.texte} flex items-center justify-center`}><Icone size={13} strokeWidth={icone === 'coche' ? 2.4 : 2} /></span>
                    <span className={`text-[13px] font-bold min-w-0 ${t.texte}`}>{titre}</span>
                </div>
            </div>
            <div className="px-4 py-3.5">
                <p className="text-[12.5px] leading-[1.55] text-content text-pretty">{texte}</p>
                {action && <div className={`mt-[11px] text-[12.5px] font-semibold ${t.texte}`}>{action}</div>}
            </div>
        </div>
    );
}

/** Le bouton plein de la zone 4 : 42 px, pleine largeur, encre — `bg-content text-surface`, jamais `bg-ink text-white`. */
export const BOUTON_PLEIN = 'w-full h-[42px] rounded-full bg-content text-surface text-[13px] font-semibold inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed';
export const BOUTON_BORDE = 'w-full h-[42px] rounded-full bg-surface border border-border text-content text-[13px] font-semibold inline-flex items-center justify-center gap-2 hover:border-content transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
